import { markRaw } from "vue";
import { eventChartTimestamp } from "./apexChart";

const RECONCILE_MS = 5 * 60 * 1000;
const OVERLAP_MS = 5 * 60 * 1000;
const MAX_ROWS = 100000;
const sameRow = (a, b) => a === b || JSON.stringify(a) === JSON.stringify(b);
const rowKey = row => row._id || `${row.device || ""}:${eventChartTimestamp(row)}`;

export function createChartHistoryLoader(fetchRows, now = Date.now) {
  const cache = new Map();
  const pending = new Map();
  let activeScope;
  return async (params, scope = "") => {
    if (activeScope !== scope) { cache.clear(); activeScope = scope; }
    const key = JSON.stringify([scope, params.deviceId, params.startDate, params.endDate, params.chartFields || ""]);
    if (pending.has(key)) return pending.get(key);
    const previous = cache.get(key);
    const stamp = now();
    const today = new Date(stamp);
    today.setHours(0, 0, 0, 0);
    const live = new Date(params.endDate).getTime() >= today.getTime();
    if (previous && (stamp - previous.checkedAt < 2000 ||
        (!live && stamp - previous.loadedAt < RECONCILE_MS))) {
      cache.delete(key); cache.set(key, previous);
      return previous.rows;
    }
    const incremental = previous && live && previous.rows.length && stamp - previous.loadedAt < RECONCILE_MS;
    const request = { ...params, chart: 1 };
    if (incremental) {
      const latest = eventChartTimestamp(previous.rows[previous.rows.length - 1]);
      if (latest !== null) request.since = new Date(latest - OVERLAP_MS).toISOString();
    }
    const promise = (async () => {
      const response = await fetchRows(request);
      const body = response && response.body !== undefined ? response.body : response;
      // Do not silently replace a valid history with an invalid/failed response.
      if (!Array.isArray(body)) throw new Error("Invalid chart history response");
      const rows = body.filter(row => row && (!row.device || String(row.device) === String(params.deviceId)));
      let combined = rows;
      if (incremental) {
        const merged = new Map(previous.rows.map(row => [rowKey(row), row]));
        rows.forEach(row => {
          const old = merged.get(rowKey(row));
          merged.set(rowKey(row), old && sameRow(old, row) ? old : row);
        });
        combined = Array.from(merged.values());
      }
      combined.sort((a, b) => eventChartTimestamp(a) - eventChartTimestamp(b) || String(a._id || "").localeCompare(String(b._id || "")));
      if (previous && combined.length === previous.rows.length &&
          combined.every((row, i) => sameRow(row, previous.rows[i]))) combined = previous.rows;
      const result = markRaw(combined);
      // A response from an earlier login must not repopulate the new scope.
      if (activeScope === scope) {
        cache.delete(key);
        cache.set(key, { rows: result, checkedAt: stamp, loadedAt: incremental ? previous.loadedAt : stamp });
        let total = Array.from(cache.values()).reduce((sum, entry) => sum + entry.rows.length, 0);
        while (cache.size > 8 || total > MAX_ROWS) {
          const oldest = cache.keys().next().value;
          total -= cache.get(oldest).rows.length;
          cache.delete(oldest);
        }
      }
      return result;
    })();
    pending.set(key, promise);
    try { return await promise; } finally { pending.delete(key); }
  };
}

// Each chart receives its own response instead of reading the last device's
// shared Vuex array. Ignore responses arriving after a device/date-range change.
export async function loadDashboardChartRows(vm, chartFields, action = "getLocationEventsIndex") {
  const params = { deviceId: vm.device._id, startDate: vm.startDate, endDate: vm.endDate };
  const generation = vm._chartLoadGeneration = (vm._chartLoadGeneration || 0) + 1;
  let rows;
  try { rows = await vm[action]({ ...params, chart: 1, chartFields }); }
  catch (error) { console.warn("Chart history refresh failed", error); return null; }
  if (vm._isDestroyed || generation !== vm._chartLoadGeneration ||
      params.deviceId !== vm.device._id || params.startDate !== vm.startDate || params.endDate !== vm.endDate) return null;
  if (rows === vm._chartEventRows) return null;
  vm._chartEventRows = rows;
  return rows;
}
